import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import {
  Coordinate,
  PmBuildingType,
  SalesClient,
  SalesListingDetailViewModel,
  SalesListingOverviewViewModel,
  SalesListingRequest,
} from '@core/api';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  from,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { LocationResult, SearchService } from '../../services/search.service';
import { ViewMode } from '../../types/view-mode.types';

export interface ISalesListingDetailViewModel
  extends SalesListingDetailViewModel {
  city?: string;
}

@Component({
  selector: 'app-sales-search',
  templateUrl: './sales-search.component.html',
  styleUrl: './sales-search.component.scss',
  standalone: false,
})
export class SalesSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('header') header!: ElementRef<HTMLHeadingElement>;
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  public currentView: ViewMode = 'list';
  private readonly searchSubject = new Subject<string>();
  private readonly sliderSubject = new Subject<number>();
  locations: Coordinate[];
  tiles: SalesListingOverviewViewModel[] = [];
  sliderValue: number = 15;
  inputValue: string = '';
  isLoading: boolean = false;
  saleSearchElement: ElementRef;
  selectedBuildingType: string | undefined = undefined;

  buildingTypes: PmBuildingType[];

  isSelectOpen = false;

  pageIndex = 0;
  savedPage = 0;

  resetted: boolean = false;

  searchControl = new FormControl('');
  filteredSuggestions: Observable<LocationResult[]>;

  private currentSuggestions: LocationResult[] = [];

  constructor(
    private readonly searchService: SearchService,
    private readonly salesClient: SalesClient,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadSaleListings();
    this.loadBuildingTypes();
    this.setupSearchSubject();
    this.setupSliderSubject();
    this.cd.detectChanges();

    this.filteredSuggestions = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: any) => {
        const searchValue =
          typeof value === 'string' ? value : value?.name || '';
        return this.filterSuggestions(searchValue);
      })
    );

    this.filteredSuggestions.subscribe(suggestions => {
      this.currentSuggestions = suggestions;
    });
  }

  ngAfterViewInit(): void {
    this.restoreFilterState();
    sessionStorage.removeItem('salesSearchFilterState');
  }

  private saveFilterState(): void {
    const filterState = {
      inputValue: this.inputValue,
      sliderValue: this.sliderValue,
      selectedBuildingType: this.selectedBuildingType,
      locations: this.locations,
      currentView: this.currentView,
      savedPage: this.savedPage,
    };
    sessionStorage.setItem(
      'salesSearchFilterState',
      JSON.stringify(filterState)
    );
  }

  private restoreFilterState(): void {
    const savedState = sessionStorage.getItem('salesSearchFilterState');
    if (savedState) {
      const filterState = JSON.parse(savedState);
      this.inputValue = filterState.inputValue || '';
      this.sliderValue = filterState.sliderValue || 15;
      this.locations = filterState.locations;
      this.currentView = filterState.currentView || 'list';
      this.savedPage = filterState.savedPage || 0;

      if (this.inputValue) {
        this.searchControl.setValue(this.inputValue, { emitEvent: false });
      }

      if (filterState.selectedBuildingType) {
        setTimeout(() => {
          this.selectedBuildingType = filterState.selectedBuildingType;
          this.cd.detectChanges();
        }, 300);
      }
    }
  }

  /**
   * Sets up the search subject to handle changes in the search query.
   * The subject is debounced to prevent excessive calls and only distinct values are processed.
   * When the search query changes, it triggers the retrieval of coordinates and the loading of sale listings.
   * If the query is empty or an error occurs, the coordinates are set to [0, 0].
   */
  private setupSearchSubject(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap(query => {
          if (!query) {
            return of(null); // Reset coordinates to null if the query is empty
          } else {
            return from(this.searchService.getCoordinatesFromQuery(query)).pipe(
              catchError(error => {
                console.error(error);
                return of(null); // Reset coordinates to null in case of error
              })
            );
          }
        })
      )
      .subscribe({
        next: result => {
          this.locations = result?.map(r => r.coordinates);
          this.loadSaleListings();
        },
        error: error => {
          console.log(error);
        },
      });
  }

  /****
   * Sets up the slider subject to handle changes in the slider value.
   * The subject is debounced to prevent excessive calls and only distinct values are processed.
   * When the slider value changes and the coordinates are not [0, 0], it triggers the loading of sale listings.
   */
  private setupSliderSubject(): void {
    this.sliderSubject
      .pipe(
        debounceTime(500), // Adjust the debounce time as needed
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (this.locations && this.locations.length > 0) {
          this.loadSaleListings();
        }
      });
  }

  ngOnDestroy(): void {
    this.searchSubject.unsubscribe();
    this.sliderSubject.unsubscribe();
  }

  /**
   * Handles the selection of a sale listing item.
   * @param item The selected sale listing overview item.
   */
  onItemSelect(item: SalesListingOverviewViewModel) {
    this.saveFilterState();
    this.router.navigate(['/landing/sale', item.id]);
  }

  /**
   * Loads the sale listings based on the current coordinates and slider value.
   */
  loadSaleListings(): void {
    this.resetted = false;

    this.isLoading = true;
    const request: SalesListingRequest = {
      locations: this.locations,
      radius: this.sliderValue,
    };
    this.salesClient.getAllSalesListingObjects(request).subscribe({
      next: data => {
        this.tiles = data.reverse();
        if (
          this.selectedBuildingType &&
          this.selectedBuildingType !== 'Alle Gebäudearten'
        ) {
          this.tiles = this.tiles.filter(tile =>
            tile.buildingTypes.some(
              bt => bt.id.toString() === this.selectedBuildingType.toString()
            )
          );
        }
        this.isLoading = false;
      },
      error: error => {
        console.error('Error loading sale listings:', error);
        this.isLoading = false;
      },
    });
  }

  loadBuildingTypes(): void {
    this.salesClient.getBuildingTypes().subscribe({
      next: data => {
        this.buildingTypes = data;
        this.cd.detectChanges();
      },
      error: error => {
        console.error('Error loading building types:', error);
      },
    });
  }

  /**
   * Sets the input value and triggers the search subject with the new value.
   * @param event The input event containing the new value.
   */
  setInputValue(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.inputValue = inputElement.value;
    this.searchSubject.next(inputElement.value);
    this.savedPage = 0;
    this.isLoading = true;
  }

  /**
   * Sets the slider value based on the input event.
   * @param event The input event containing the new slider value.
   */
  setSliderValue(value: number) {
    if (this.sliderValue === value) {
      return; // Keine Änderung, Event ignorieren
    }

    this.sliderValue = value;
    if (this.resetted === false) {
      this.sliderSubject.next(this.sliderValue);
      this.savedPage = 0;
    }
  }

  public setView(view: ViewMode): void {
    this.currentView = view;
  }

  public resetFilter(): void {
    if (this.inputValue !== '') {
      this.resetted = true;
      this.inputValue = '';
      this.searchControl.setValue('', { emitEvent: false });
      this.selectedBuildingType = undefined;
      this.sliderValue = 15;
      this.savedPage = 0;
      this.isLoading = true;
      this.searchSubject.next(null);
    } else if (this.selectedBuildingType !== undefined) {
      this.sliderValue = 15;
      this.selectedBuildingType = undefined;
      this.loadSaleListings();
    } else if ((this.inputValue === '' && this.sliderValue) !== 15) {
      this.resetted = true;
      this.sliderValue = 15;
    }
    sessionStorage.removeItem('salesSearchFilterState');
  }

  onBuildingTypeChange(event: MatSelectChange) {
    this.savedPage = 0;
    this.selectedBuildingType = event.value;
    this.loadSaleListings();
  }

  @HostListener('window:scroll')
  onWinScroll() {
    const root = this.header.nativeElement;
    const headerBar = root.querySelector('.header-item');
    const headerBackground = root.querySelector('.header-background');

    if (!headerBar || !headerBackground) {
      return;
    }

    const stickyStart = root.getBoundingClientRect().top;
    if (stickyStart <= 70) {
      headerBar.classList.add('is-stuck');
      headerBackground.classList.add('is-stuck');
      root.classList.add('has-sticky-spacer');
    } else {
      headerBar.classList.remove('is-stuck');
      headerBackground.classList.remove('is-stuck');
      root.classList.remove('has-sticky-spacer');
    }
  }

  get canDelete(): boolean {
    return !(
      this.inputValue !== '' ||
      this.sliderValue !== 15 ||
      this.selectedBuildingType !== undefined
    );
  }

  private filterSuggestions(searchTerm: string): Observable<LocationResult[]> {
    if (!searchTerm || searchTerm?.length < 2) {
      return new Observable(observer => observer.next([]));
    }
    return this.searchService.getSuggestions(searchTerm);
  }

  private optionSelected = false;

  onOptionSelected(event: any) {
    this.optionSelected = true;

    const selectedSuggestion = event.option.value;

    const searchValue =
      typeof selectedSuggestion === 'string'
        ? selectedSuggestion
        : selectedSuggestion.name;

    this.inputValue = searchValue;
    this.cd.detectChanges();

    this.performSearch(selectedSuggestion);

    this.searchControl.setValue(searchValue, { emitEvent: false });

    this.savedPage = 0;
    this.isLoading = true;

    setTimeout(() => {
      this.optionSelected = false;
    }, 100);
  }

  private performSearch(selectedSuggestion: LocationResult) {
    this.locations = [selectedSuggestion.coordinates];
    this.loadSaleListings();
  }

  onEnterPressed(event: any) {
    if (this.optionSelected) {
      return;
    }

    if (this.currentSuggestions && this.currentSuggestions.length > 0) {
      event.preventDefault();
      const firstSuggestion = this.currentSuggestions[0];

      const mockEvent = {
        option: {
          value: firstSuggestion,
        },
      };
      this.onOptionSelected(mockEvent);

      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }
  }
}
